import { useId, useEffect } from 'react';
import {
  mergePickerItems,
  loadTechnicalReportSets,
  type PickerItem,
} from '@/components/modals/formPickerItems';
import { Plus, List } from 'lucide-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { Field, Form, Formik } from 'formik';
import { useRef, useState } from 'react';
import Bar from '../../../components/elements/Bar';
import FormList from '../../../components/elements/FormList';
import { InputField } from '../../../components/formFields/InputField';
import { SelectFormField } from '../../../components/formFields/SelectFormField';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import PickClassificationPresetModal from '../../../components/modals/PickClassificationPresetModal';
import PickValueWithClassificationModal from '../../../components/modals/PickValueWithClassificationModal';
import Label from '@/components/Label';
import { useGetBuildingObjects, useGetTemplates } from '../hooks/getDropdownOptions';
import useGetFilter from '../hooks/getFilter';
import { apiService } from '../../../utils/api';
import * as Yup from 'yup';
import classNames from 'classnames';
import { SpinnerOverlay } from '@/components/ui/Spinner';

interface PrintSettingsModalProps {
  filterId: any;
  onSubmit: (id: any, values: any) => void;
  visible: boolean;
  onClose: () => void;
  document: any;
}

const PrintSettingsModalForm: React.FC<PrintSettingsModalProps> = ({
  filterId,
  onSubmit,
  visible,
  onClose,
  document,
}) => {
  const pickerSession = useId();
  const formRef = useRef<any>();
  const { data, isLoading } = useGetFilter(filterId);
  const { data: buildingObjects } = useGetBuildingObjects();
  const { data: templates } = useGetTemplates();

  const ValidationSchema = Yup.object().shape({
    title: Yup.string().nullable().required('Povinné pole'),
    classification: Yup.array()
      .of(Yup.object())
      .min(1, 'Minimálně jenda položka vyžadována')
      .required('Povinné pole'),
    template: Yup.string().nullable().required('Povinné pole'),
    building_object: Yup.string().nullable().required('Povinné pole'),
  });

  const [visibleAddTechnicalReportItem, setVisibleAddTechnicalReportItem] = useState(false);
  const [visibleClassificationModal, setVisibleClassificationModal] = useState(false);
  const [visibleAddTechnicalReportItemSetDialog, setVisibleAddTechnicalReportItemSetDialog] =
    useState(false);

  const handleAddTechnicalReportItem = async (items: PickerItem[]) => {
    await formRef.current.setFieldValue(
      'classified_technical_report_item_set',
      mergePickerItems(formRef.current.values.classified_technical_report_item_set, items)
    );
  };

  const handleDeleteTechnicalReportItem = (id: any) => {
    if (!formRef?.current) return null;

    const newValues = formRef.current.values.classified_technical_report_item_set.filter(
      (item: any) => item.id !== id
    );

    formRef.current.setFieldValue('classified_technical_report_item_set', newValues);
  };

  const handleAddClassificationPresets = (items: any[]) => {
    const uniq = [...formRef.current.values.classification, ...items].filter(
      (obj: any, index: number, self: any[]) => index === self.findIndex((o) => o.id === obj.id)
    );

    formRef.current.setFieldValue('classification', uniq);
    setVisibleClassificationModal(false);
  };

  const handleDeleteClassificationItem = (id: any) => {
    if (!formRef?.current) return null;

    const newValues = formRef.current.values.classification.filter((item: any) => item.id !== id);

    formRef.current.setFieldValue('classification', newValues);
  };

  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const handleAddTechnicalReportItemSet = async (items: PickerItem[]) => {
    try {
      const added = await loadTechnicalReportSets(items, (url) => apiService.get(url));
      if (!active.current || !formRef.current) return;
      await formRef.current.setFieldValue(
        'classified_technical_report_item_set',
        mergePickerItems(formRef.current.values.classified_technical_report_item_set, added)
      );
    } catch {
      return {
        error:
          'Sady se nepodařilo načíst. Formulář zůstal beze změny. Zkuste výběr potvrdit znovu.',
      };
    }
  };

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{filterId ? 'Editace nastavení tisku' : 'Nastavení tisku'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading ? (
            <div className="h-full w-full">
              <SpinnerOverlay />
            </div>
          ) : (
            <div>
              <Formik
                innerRef={formRef}
                enableReinitialize={true}
                validationSchema={ValidationSchema}
                initialValues={{
                  title: data?.title || document.title || null,
                  common_note: data?.common_note || null,
                  concept_description: data?.concept_description || null,
                  classified_technical_report_item_set:
                    data?.classified_technical_report_item_set || [],
                  classification: data?.classification || [],
                  template: data?.template || null,
                  building_object: data?.building_object || null,
                }}
                onSubmit={(values) => {
                  const classification = values.classification.map((item: any) => item.id);
                  const reportItemSet = values.classified_technical_report_item_set.map(
                    (item: any) => item.id
                  );
                  onSubmit(filterId, {
                    ...values,
                    classification,
                    classified_technical_report_item_set: reportItemSet,
                  });
                }}
              >
                {(props) => {
                  const { values, setFieldValue, errors } = props;

                  return (
                    <Form className="space-y-1">
                      <div>
                        <Label name="title" title="Název filtru" required />

                        <Field
                          name="title"
                          value={values.title || null}
                          placeholder=""
                          disabled={data}
                          component={InputField}
                        />
                      </div>
                      <div>
                        <Label name="template" title="Šablona" required />

                        <SelectFormField name="template" placeholder="" data={templates ?? []} />
                      </div>

                      <div className="mb-4 mt-2">
                        <div
                          className={classNames({ 'border-red-400 border': errors.classification })}
                        >
                          <Bar
                            text="Kategorie"
                            content={[
                              <Plus
                                key={1}
                                className="h-4 w-4"
                                data-testid="print-settings-add-classification-btn"
                                onClick={() => setVisibleClassificationModal(true)}
                              />,
                            ]}
                          />
                        </div>
                        <FormList
                          data={values.classification}
                          onDelete={handleDeleteClassificationItem}
                        />
                      </div>

                      <div>
                        <Label name="building_object" title="Stavební objekt" required />

                        <SelectFormField
                          name="building_object"
                          placeholder=""
                          data={buildingObjects ?? []}
                        />
                      </div>

                      <div>
                        <Label name="concept_description" title="Popis koncepce" />
                        <div
                          className={classNames({
                            'border-red-400 border': errors.concept_description,
                          })}
                        >
                          <CKEditor
                            data={values.concept_description ?? ''}
                            editor={ClassicEditor}
                            config={{ licenseKey: 'GPL' }}
                            onChange={(_e: any, editor: any) => {
                              const data = editor.getData();
                              setFieldValue('concept_description', data);
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <Label name="common_note" title="Společná poznámka" />
                        <div
                          className={classNames({ 'border-red-400 border': errors.common_note })}
                        >
                          <CKEditor
                            data={values.common_note ?? ''}
                            editor={ClassicEditor}
                            config={{ licenseKey: 'GPL' }}
                            onChange={(_e: any, editor: any) => {
                              const data = editor.getData();
                              setFieldValue('common_note', data);
                            }}
                          />
                        </div>
                      </div>

                      <div className="mb-4 mt-2">
                        <div
                          className={classNames({
                            'border-red-400 border': errors.classified_technical_report_item_set,
                          })}
                        >
                          <Bar
                            text="Prvky technické zprávy"
                            content={[
                              <Plus
                                key={1}
                                className="h-4 w-4"
                                data-testid="print-settings-add-report-item-btn"
                                onClick={() => setVisibleAddTechnicalReportItem(true)}
                              />,
                              <List
                                key={2}
                                className="h-4 w-4"
                                data-testid="print-settings-add-report-item-set-btn"
                                onClick={() => setVisibleAddTechnicalReportItemSetDialog(true)}
                              />,
                            ]}
                          />
                        </div>
                        <FormList
                          data={values.classified_technical_report_item_set}
                          onDelete={handleDeleteTechnicalReportItem}
                        />
                      </div>
                      <PickValueWithClassificationModal
                        multiple
                        sessionKey={pickerSession}
                        visible={visibleAddTechnicalReportItem}
                        url="left-panel-technical-report-item/"
                        pickerUrl="technical-report-item/left-panel-picker/"
                        onClose={() => setVisibleAddTechnicalReportItem(false)}
                        onSubmit={handleAddTechnicalReportItem}
                        alreadySelectedValues={values.classified_technical_report_item_set}
                      />
                      <PickClassificationPresetModal
                        visible={visibleClassificationModal}
                        value={values.classification}
                        onClose={() => setVisibleClassificationModal(false)}
                        onSubmit={handleAddClassificationPresets}
                      />
                      <PickValueWithClassificationModal
                        multiple
                        sessionKey={pickerSession}
                        visible={visibleAddTechnicalReportItemSetDialog}
                        url="left-panel-set-technical-report-item/"
                        pickerUrl="set-technical-report-item/left-panel-picker/"
                        onClose={() => setVisibleAddTechnicalReportItemSetDialog(false)}
                        onSubmit={handleAddTechnicalReportItemSet}
                        alreadySelectedValues={[]}
                      />
                    </Form>
                  );
                }}
              </Formik>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            key={1}
            variant="outline"
            data-testid="print-settings-close-btn"
            onClick={() => onClose()}
          >
            Zavřít
          </Button>
          <Button
            type="submit"
            key={2}
            data-testid="print-settings-confirm-btn"
            onClick={() => formRef.current?.submitForm()}
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function PrintSettingsModal(props: React.ComponentProps<typeof PrintSettingsModalForm>) {
  return props.visible ? (
    <PrintSettingsModalForm
      key={`${props.document?.id ?? 'document'}:${props.filterId ?? 'create'}`}
      {...props}
    />
  ) : null;
}

export default PrintSettingsModal;
